import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Bell, Link2, Copy, Share2 } from "lucide-react";
import { payBill, createBillSignLink } from "../../api/accounting";
import { NeuButton } from "../../components/neu/Neu";
import { useApiError } from "../../hooks/useApiError";
import { localName } from "../../lib/localName";

const writeClipboard = async (t) => { try { await navigator.clipboard.writeText(t); return true; } catch { return false; } };

// نافذة تأكيد الإرسال — تصميم موحّد داخلياً وخارجياً، مع تبديل الزر (إشعار / رابط).
export const BillPaySendDialog = ({ bill, method, cashboxId, chosenBox, external,
                                     receiverName, receiverPhone, receiverUserId, onCancel, onDone }) => {
  const { t, i18n } = useTranslation();
  const describe = useApiError();
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  const send = async () => {
    setBusy(true);
    try {
      await payBill(bill.id, method, method !== "credit" ? cashboxId : null,
                     receiverName || null, receiverPhone || null, receiverUserId);
      if (external) {
        const lang = (i18n.language || "ar").startsWith("en") ? "en" : "ar";
        const r = await createBillSignLink(bill.id, lang);
        setLink(r.url); await writeClipboard(r.url);
        toast.success(t("acct.bills.send.linkReady"));
      } else {
        toast.success(t("acct.bills.send.pushSent")); onDone();
      }
    } catch (e) { toast.error(describe(e)); } finally { setBusy(false); }
  };
  const copy = async () => { if (link && await writeClipboard(link)) toast.success(t("acct.bills.share.copied")); };
  const share = async () => {
    if (!link) return;
    if (navigator.share) { try { await navigator.share({ url: link }); } catch {} } else await copy();
  };

  const boxLabel = chosenBox ? `${chosenBox.kind === "bank" ? "🏦" : "💰"} ${localName(chosenBox)}`
                             : t("acct.methods.credit");
  return (
    <div className="fixed inset-0 z-50 grid place-content-center bg-black/60 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} data-testid="bill-pay-send-dialog"
           className="w-[min(640px,95vw)] max-h-[92vh] overflow-y-auto rounded-3xl bg-background shadow-2xl">
        <div className="rounded-t-3xl bg-blue-700 p-6 text-white">
          <h3 className="text-lg font-black">{external ? t("acct.bills.send.titleLink") : t("acct.bills.send.title")}</h3>
        </div>
        <div className="grid gap-2 p-6 text-sm">
          <Row label={t("acct.bills.send.party")}    value={bill.description || "—"} />
          <Row label={t("acct.bills.send.amount")}   value={`${bill.total} ${bill.currency || ""}`} bold />
          <Row label={t("acct.bills.send.pmethod")}  value={boxLabel} />
          <Row label={t("acct.bills.send.receiver")} value={receiverName || "—"} />
          <div className="mt-3 rounded-2xl border-2 border-blue-500/30 bg-blue-500/5 p-3 text-xs">
            <p className="mb-2 font-black text-blue-700">📌 {t("acct.bills.send.whatHappens")}</p>
            <ol className="ms-5 list-decimal space-y-1 font-bold text-blue-900">
              <li>{external ? t("acct.bills.send.step1link") : t("acct.bills.send.step1push", { name: receiverName })}</li>
              <li>{t("acct.bills.send.step2")}</li>
              <li>{t("acct.bills.send.step3")}</li>
              <li>{t("acct.bills.send.step4")}</li>
            </ol>
          </div>
          {link ? (
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-3">
              <p className="mb-2 text-xs font-black text-primary">{t("acct.bills.send.linkReady")}</p>
              <p className="mb-3 break-all rounded-xl bg-background p-2 font-mono text-[11px]" dir="ltr">{link}</p>
              <div className="flex gap-2">
                <NeuButton data-testid="send-copy-link" onClick={copy}><Copy className="h-4 w-4" /> {t("acct.bills.share.copy")}</NeuButton>
                <NeuButton data-testid="send-share-link" onClick={share}><Share2 className="h-4 w-4" /> {t("acct.bills.share.action")}</NeuButton>
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-3 border-t border-border/40 p-4">
          <NeuButton data-testid="send-cancel" onClick={link ? onDone : onCancel}>
            {link ? t("app.done") : t("app.cancel")}
          </NeuButton>
          {!link ? (
            <button data-testid="send-confirm" onClick={send} disabled={busy}
                    className="flex items-center gap-2 rounded-2xl bg-blue-700 px-6 py-2 text-sm font-black text-white shadow-md hover:bg-blue-800 disabled:opacity-50">
              {external ? <Link2 className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
              {external ? t("acct.bills.send.ctaLink") : t("acct.bills.send.ctaPush")}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, bold }) => (
  <div className="flex items-baseline justify-between gap-2">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={bold ? "font-black" : "font-bold"}>{value}</span>
  </div>
);
